import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { GiCrossedBones } from 'react-icons/gi';
import { LuSave } from 'react-icons/lu';
import InsurerDropdown from "../../../../utils/Insurer";
import { IoUmbrellaOutline } from "react-icons/io5";
import { FaHeart } from "react-icons/fa";
import { ShieldCheck } from "lucide-react";
import { toast } from "react-toastify";
import { useGetAgencyByIdQuery, useSaveAgencyMutation } from "../../../../services/redux/api/components";

const AddAgency = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = Boolean(id);   // Determine if in edit mode based on presence of id

  const [agencyType, setAgencyType] = useState("");
  const [insurerType, setInsurerType] = useState("L");

  const [saveAgency] = useSaveAgencyMutation();
  const { data: agencyData, isLoading } = useGetAgencyByIdQuery(id, { skip: !isEditMode });

  const initialValues = {
    agencyType: agencyData?.data?.AgencyType || "",
    agencyName: agencyData?.data?.AgencyName || "",
    agencyShortName: agencyData?.data?.AgencyShortName || "",
    InsurerId: agencyData?.data?.InsurerId || "",
    agencyCode: agencyData?.data?.AgencyCode || "",
    agencyRenewalDate: agencyData?.data?.AgencyRenewalDate
      ? agencyData.data?.AgencyRenewalDate.slice(0, 10)
      : "",
  };

  const validationSchema = Yup.object().shape({
    agencyName: Yup.string().required("Agency Name is required"),
    InsurerId: Yup.string().required("Insurer is required"),
    agencyCode: Yup.string().required("Agency Code is required"),
    agencyRenewalDate: Yup.string().required("Agency Renewal Date is required"),
  });

  useEffect(() => {
    if (agencyData?.data?.AgencyType) {
      const type = Array.isArray(agencyData.data.AgencyType)
        ? agencyData.data.AgencyType[0]
        : agencyData.data.AgencyType;

      setAgencyType(type);
      const map = { life: "L", health: "H", general: "G" };
      setInsurerType(map[type]);
    }
  }, [agencyData]);

  const handleSubmit = async (values, { resetForm }) => {
    const payload = {
      ...values,
      mode: isEditMode ? "Update" : "Add",
      AgencyType: agencyType,
      // InsurerId: values.insurer, // Ensure this holds the Insurer_ID
    }
    if (isEditMode) {
      payload.AgencyId = id;
    }
    await saveAgency(payload)
      .then((response) => {
        console.log("API Response:", response); // Check the response from the API
        if (response?.data?.success) {
          toast.success(response.data.message || (isEditMode ? "Agency Updated Successfully" : "Agency added successfully!"));
          resetForm();
          setAgencyType("");
          window.history.back();
        } else {
          toast.error(response?.data?.message || "Failed to add agency. Please try again.");
        }
      })
      .catch((error) => {
        console.error("Error during API call:", error);
        toast.error("An error occurred while adding the agency. Please try again.");
      });

  };

  if (isEditMode && isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <div className="formContainer lg:p-4">
      <div className="flex flex-col p-4 lg:mx-5 border rounded bg-white" style={{ boxShadow: "0px 0px 12px #cac8c8" }}>
        <h5 className="text-brandRed font-bold border-b border-brandRed pb-2 mb-4 px-2">Agency Master</h5>

        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting, values }) => (
            <Form id="agencyForm">
              <div className="mb-4">
                <label className="form-label">Agency Type</label>
                <div className="d-flex gap-3 mt-2">
                  {[
                    { type: "life", insurerType: 'L', icon: <IoUmbrellaOutline size={24} />, label: "Life" },
                    { type: "health", insurerType: 'H', icon: <FaHeart size={24} />, label: "Health" },
                    { type: "general", insurerType: 'G', icon: <ShieldCheck size={24} />, label: "General" }
                  ].map(({ type, icon, insurerType, label }) => (
                    <button
                      key={type}
                      type="button"
                      className={`w-[60px] h-[60px] border-2 ${agencyType === type ? "bg-brandRed border-4 border-brandRed text-white" : "border-brandRed text-brandRed"} rounded-full flex items-center justify-center`}
                      onClick={() => {
                        setAgencyType(type);
                        setInsurerType(insurerType);
                      }}
                      title={label}
                    >
                      {icon}
                    </button>
                  ))}
                </div>
              </div>

              <div className="row g-3">
                <div className="col-md-6">
                  <label className="form-label">Agency Name  <span className="text-danger">*</span></label>
                  <Field type="text" name="agencyName" className="form-control custom-shadow" />
                  <ErrorMessage name="agencyName" component="div" className="text-brandRed" />
                </div>

                <div className="col-md-6">
                  <label className="form-label">Agency Short Name</label>
                  <Field type="text" name="agencyShortName" className="form-control custom-shadow" />
                </div>

                <div className="col-md-6">
                  <InsurerDropdown name="InsurerId" insurerType={insurerType} className="form-control custom-shadow" />
                </div>
                <div className="col-md-6">
                  <label className="form-label">Agency Code  <span className="text-danger">*</span></label>
                  <Field type="text" name="agencyCode" className="form-control custom-shadow" />
                  <ErrorMessage name="agencyCode" component="div" className="text-brandRed" />
                </div>

                <div className="col-md-6">
                  <label className="form-label">Agency Renewal Date  <span className="text-danger">*</span></label>
                  <Field type="date" name="agencyRenewalDate" className="form-control custom-shadow" />
                  <ErrorMessage name="agencyRenewalDate" component="div" className="text-brandRed" />
                </div>
              </div>

              <div className="d-flex justify-content-center gap-3 mt-5">
                <button type="submit" className="saveButton d-flex align-items-center px-4" disabled={isSubmitting}>
                  <LuSave className="me-2" />
                  {isSubmitting ? "Saving..." : "Save"}
                </button>
                <button type="button" className="backButton d-flex align-items-center px-4" onClick={() => window.history.back()}>
                  <GiCrossedBones className="me-2" />
                  Cancel
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};

export default AddAgency;
